import { apiClient } from '@/core/api/client';
import type {
  TrackerEmail,
  EmailsResponse,
  EmailResponse,
  EmailUpdateRequest,
  EmailAssociateRequest,
  EmailAssignRequest,
  EmailDiscardRequest,
  VoicemailTaskRequest,
  VoicemailTaskResponse,
  EmailProcessRequest,
  EmailProcessResponse,
  AffaireCreationStats,
  EmailDuplicate,
  TriageStats
} from '../types/tracker.types';

/**
 * Email API for Tracker Integration
 * Uses apiClient which automatically includes Bearer token from localStorage
 */
export const emailsAPI = {
  /**
   * Get emails by affaire case number
   */
  async getEmailsByAffaire(caseNumber: string, includeDiscarded = false): Promise<EmailsResponse> {
    return apiClient.get<EmailsResponse>(`/api/admin/emails/affaire/${caseNumber}`, {
      params: { includeDiscarded: includeDiscarded.toString() }
    });
  },

  /**
   * Get emails by affaire ID
   */
  async getEmailsByAffaireId(affaireId: number, includeDiscarded = false): Promise<EmailsResponse> {
    return apiClient.get<EmailsResponse>(`/api/admin/emails/affaire-id/${affaireId}`, {
      params: { includeDiscarded: includeDiscarded.toString() }
    });
  },

  /**
   * Get unassigned emails (no case_number)
   */
  async getUnassignedEmails(params?: {
    includeDiscarded?: boolean;
    discardedOnly?: boolean;
    triage_flag?: string;
    limit?: number;
    offset?: number;
  }): Promise<EmailsResponse> {
    const queryParams: Record<string, string | number> = {
      includeDiscarded: params?.includeDiscarded?.toString() || 'false',
      discardedOnly: params?.discardedOnly?.toString() || 'false',
      limit: params?.limit || 100,
      offset: params?.offset || 0
    };
    if (params?.triage_flag) {
      queryParams.triage_flag = params.triage_flag;
    }
    return apiClient.get<EmailsResponse>('/api/admin/emails/unassigned', {
      params: queryParams
    });
  },

  /**
   * Get triage stats for filter badges
   */
  async getTriageStats(): Promise<{ success: boolean; data: TriageStats }> {
    return apiClient.get('/api/admin/emails/triage-stats');
  },

  /**
   * Get single email by ID
   */
  async getEmailById(emailId: string): Promise<EmailResponse> {
    return apiClient.get<EmailResponse>(`/api/admin/emails/${emailId}`);
  },

  /**
   * Update email status (read/unread, task status)
   */
  async updateEmailStatus(emailId: string, updates: EmailUpdateRequest): Promise<EmailResponse> {
    return apiClient.patch<EmailResponse>(`/api/admin/emails/${emailId}/status`, updates);
  },

  /**
   * Associate email to affaire
   */
  async associateEmailToAffaire(emailId: string, data: EmailAssociateRequest): Promise<EmailResponse> {
    return apiClient.post<EmailResponse>(`/api/admin/emails/${emailId}/associate`, data);
  },

  /**
   * Discard email
   */
  async discardEmail(emailId: string, data: EmailDiscardRequest): Promise<EmailResponse> {
    return apiClient.post<EmailResponse>(`/api/admin/emails/${emailId}/discard`, data);
  },

  /**
   * Ban a sender — discards all their emails and auto-discards future ones
   */
  async banSender(emailAddress: string, reason?: string): Promise<EmailResponse> {
    return apiClient.post<EmailResponse>('/api/admin/emails/ban-sender', {
      email_address: emailAddress,
      reason: reason || 'Spam'
    });
  },

  /**
   * Assign email to user or role
   * @deprecated Use processEmail instead for unified email handling
   */
  async assignEmail(emailId: string, data: EmailAssignRequest): Promise<EmailResponse> {
    return apiClient.post<EmailResponse>(`/api/admin/emails/${emailId}/assign`, data);
  },

  /**
   * Process email: Link to affaire OR create task
   * If affaire_id provided: Email is linked to affaire (appears in tracker)
   * If no affaire_id: Creates task WITHOUT k_id_affaire (appears in mes-tâches)
   */
  async processEmail(emailId: string, data: EmailProcessRequest): Promise<EmailProcessResponse> {
    return apiClient.post<EmailProcessResponse>(`/api/admin/emails/${emailId}/process`, data);
  },

  /**
   * Get email attachments
   */
  async getEmailAttachments(emailId: string): Promise<{ success: boolean; data: any[]; count: number }> {
    return apiClient.get<{ success: boolean; data: any[]; count: number }>(
      `/api/admin/emails/${emailId}/attachments`
    );
  },

  /**
   * Search emails
   */
  async searchEmails(params: {
    query?: string;
    sender?: string;
    subject?: string;
    from_date?: string;
    to_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<EmailsResponse> {
    return apiClient.get<EmailsResponse>('/api/admin/emails/search', { params });
  },

  /**
   * Create a task from a voicemail email
   */
  async createVoicemailTask(emailId: string, data: VoicemailTaskRequest): Promise<VoicemailTaskResponse> {
    return apiClient.post<VoicemailTaskResponse>(`/api/admin/emails/${emailId}/create-voicemail-task`, data);
  },

  /**
   * Get affaire creation task stats per user
   * Returns count of active (not done) and total (last 7 days) affaire creation tasks
   */
  async getAffaireCreationStats(): Promise<{ success: boolean; data: AffaireCreationStats }> {
    return apiClient.get<{ success: boolean; data: AffaireCreationStats }>('/api/admin/emails/affaire-creation-stats');
  },

  /**
   * Reallocate email to a different affaire
   */
  async reallocateEmail(emailId: string, targetAffaireId: number): Promise<EmailResponse> {
    return apiClient.post<EmailResponse>(`/api/admin/emails/${emailId}/reallocate`, {
      target_affaire_id: targetAffaireId
    });
  },

  /**
   * Assign email to multiple affaires (creates duplicates)
   */
  async assignToMultipleAffaires(
    emailId: string,
    data: { affaire_ids: number[]; excluded_attachments?: string[] }
  ): Promise<{ success: boolean; data: TrackerEmail[]; message: string }> {
    return apiClient.post<{ success: boolean; data: TrackerEmail[]; message: string }>(
      `/api/admin/emails/${emailId}/assign-multi`,
      data
    );
  },

  /**
   * Get all duplicates of an email
   */
  async getEmailDuplicates(emailId: string): Promise<{
    success: boolean;
    data: EmailDuplicate[];
    count: number;
    message?: string;
  }> {
    return apiClient.get<{ success: boolean; data: EmailDuplicate[]; count: number; message?: string }>(
      `/api/admin/emails/${emailId}/duplicates`
    );
  },

  /**
   * Unlink email from affaire (goes back to unassigned emails)
   */
  async unlinkEmail(emailId: string): Promise<EmailResponse> {
    return apiClient.post<EmailResponse>(`/api/admin/emails/${emailId}/unlink`, {});
  },

  /**
   * Transcribe a voicemail via Whisper/Groq
   */
  async transcribeVoicemail(emailId: string): Promise<{ success: boolean; transcription: string }> {
    return apiClient.post<{ success: boolean; transcription: string }>(`/api/admin/emails/${emailId}/transcribe`, {});
  },

  /**
   * Delete email copy (soft delete)
   */
  async deleteEmailCopy(emailId: string): Promise<{
    success: boolean;
    data: { id: string; subject: string };
    message: string;
  }> {
    return apiClient.delete<{ success: boolean; data: { id: string; subject: string }; message: string }>(
      `/api/admin/emails/${emailId}`
    );
  }
};
